import { Suspense } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { ErrorBoundary, FullPageSpinner } from '@/components/common';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { AuthLayout } from '@/components/layout/AuthLayout';
import { ProtectedRoute } from '@/components/layout/ProtectedRoute';
import { WorkerLayout } from '@/components/layout/WorkerLayout';
import { useAuth } from '@/hooks';
import { lazyWithReload } from './lazy-with-reload';
import { NotFoundPage } from './NotFoundPage';
import { RouteErrorPage } from './RouteErrorPage';

// Lazy-loaded pages
const LoginPage = lazyWithReload(() => import('@/features/auth/LoginPage'));
const ForgotPasswordPage = lazyWithReload(() => import('@/features/auth/ForgotPasswordPage'));
const ResetPasswordPage = lazyWithReload(() => import('@/features/auth/ResetPasswordPage'));
const CrewInvitePage = lazyWithReload(() => import('@/features/crew-invite/CrewInvitePage'));
const TasksPage = lazyWithReload(() => import('@/features/tasks/TasksPage'));
const ShowsPage = lazyWithReload(() => import('@/features/shows/ShowsPage'));
const ShowDetailPage = lazyWithReload(() => import('@/features/shows/ShowDetailPage'));
const EpisodeDetailPage = lazyWithReload(() => import('@/features/episodes/EpisodeDetailPage'));
const WorkflowsPage = lazyWithReload(() => import('@/features/workflows/WorkflowsPage'));
const WorkflowEditorPage = lazyWithReload(() => import('@/features/workflows/WorkflowEditorPage'));
const PlannerPage = lazyWithReload(() => import('@/features/planner/PlannerPage'));
const TeamPage = lazyWithReload(() => import('@/features/team/TeamPage'));
const RolesPage = lazyWithReload(() => import('@/features/roles/RolesPage'));
const EquipmentPage = lazyWithReload(() => import('@/features/equipment/EquipmentPage'));
const EquipmentDetailPage = lazyWithReload(
  () => import('@/features/equipment/EquipmentDetailPage')
);
const EquipmentAvailabilityPage = lazyWithReload(
  () => import('@/features/equipment/EquipmentAvailabilityPage')
);
const VehiclesPage = lazyWithReload(() => import('@/features/vehicles/VehiclesPage'));
const GraphicsBoardPage = lazyWithReload(() => import('@/features/graphics/GraphicsBoardPage'));
const WorkerGraphicsPage = lazyWithReload(() => import('@/features/graphics/WorkerGraphicsPage'));
const ReportsPage = lazyWithReload(() => import('@/features/reports/ReportsPage'));
const AdminNotificationsPage = lazyWithReload(
  () => import('@/features/notifications/AdminNotificationsPage')
);
const AdminProfilePage = lazyWithReload(() => import('@/features/profile/AdminProfilePage'));
const NotificationSettingsPage = lazyWithReload(
  () => import('@/features/notifications/NotificationSettingsPage')
);
const WorkerHomePage = lazyWithReload(() => import('@/features/worker/WorkerHomePage'));
const WorkerTaskPage = lazyWithReload(() => import('@/features/worker/WorkerTaskPage'));
const WorkerNotificationsPage = lazyWithReload(
  () => import('@/features/worker/WorkerNotificationsPage')
);
const WorkerJobsPage = lazyWithReload(() => import('@/features/worker/WorkerJobsPage'));
const WorkerSchedulePage = lazyWithReload(() => import('@/features/worker/WorkerSchedulePage'));
const WorkerProfilePage = lazyWithReload(() => import('@/features/worker/WorkerProfilePage'));
const WorkerAssignPage = lazyWithReload(() => import('@/features/worker/WorkerAssignPage'));

function LazyPage({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<FullPageSpinner />}>{children}</Suspense>;
}

/**
 * The manifest's start_url points here, so the installed PWA (and any visit to
 * the bare domain) lands every user on their own home: dashboard users on the
 * admin side, crew on /worker.
 */
function RootRedirect() {
  const { isAuthenticated, isLoading, canAccessDashboard } = useAuth();

  if (isLoading) {
    return <FullPageSpinner />;
  }
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  return <Navigate to={canAccessDashboard ? '/admin/tasks' : '/worker'} replace />;
}

export const router = createBrowserRouter([
  // Auth routes
  {
    element: <AuthLayout />,
    errorElement: <RouteErrorPage />,
    children: [
      {
        path: '/login',
        element: (
          <LazyPage>
            <LoginPage />
          </LazyPage>
        ),
      },
      {
        path: '/forgot-password',
        element: (
          <LazyPage>
            <ForgotPasswordPage />
          </LazyPage>
        ),
      },
      {
        path: '/reset-password/:token',
        element: (
          <LazyPage>
            <ResetPasswordPage />
          </LazyPage>
        ),
      },
      {
        path: '/potvrda-rasporeda/:token',
        element: (
          <LazyPage>
            <CrewInvitePage />
          </LazyPage>
        ),
      },
    ],
  },

  // Admin routes
  {
    path: '/admin',
    errorElement: <RouteErrorPage />,
    element: (
      <ProtectedRoute requireAdmin>
        <AdminLayout />
      </ProtectedRoute>
    ),
    children: [
      { index: true, element: <Navigate to="/admin/tasks" replace /> },
      {
        path: 'tasks',
        element: (
          <ErrorBoundary>
            <LazyPage>
              <TasksPage />
            </LazyPage>
          </ErrorBoundary>
        ),
      },
      {
        path: 'shows',
        element: (
          <ErrorBoundary>
            <LazyPage>
              <ShowsPage />
            </LazyPage>
          </ErrorBoundary>
        ),
      },
      {
        path: 'shows/:id',
        element: (
          <ErrorBoundary>
            <LazyPage>
              <ShowDetailPage />
            </LazyPage>
          </ErrorBoundary>
        ),
      },
      {
        path: 'shows/:showId/episodes/:id',
        element: (
          <ErrorBoundary>
            <LazyPage>
              <EpisodeDetailPage />
            </LazyPage>
          </ErrorBoundary>
        ),
      },
      {
        path: 'workflows',
        element: (
          <ErrorBoundary>
            <LazyPage>
              <WorkflowsPage />
            </LazyPage>
          </ErrorBoundary>
        ),
      },
      {
        path: 'workflows/:id',
        element: (
          <ErrorBoundary>
            <LazyPage>
              <WorkflowEditorPage />
            </LazyPage>
          </ErrorBoundary>
        ),
      },
      {
        path: 'planner',
        element: (
          <ErrorBoundary>
            <LazyPage>
              <PlannerPage />
            </LazyPage>
          </ErrorBoundary>
        ),
      },
      {
        path: 'team',
        element: (
          <ErrorBoundary>
            <LazyPage>
              <TeamPage />
            </LazyPage>
          </ErrorBoundary>
        ),
      },
      {
        path: 'equipment',
        element: (
          <ErrorBoundary>
            <LazyPage>
              <EquipmentPage />
            </LazyPage>
          </ErrorBoundary>
        ),
      },
      // Graphics requests board (TASK-57), open to every dashboard user.
      {
        path: 'graphics',
        element: (
          <ErrorBoundary>
            <LazyPage>
              <GraphicsBoardPage />
            </LazyPage>
          </ErrorBoundary>
        ),
      },
      {
        path: 'equipment/availability',
        element: (
          <ErrorBoundary>
            <LazyPage>
              <EquipmentAvailabilityPage />
            </LazyPage>
          </ErrorBoundary>
        ),
      },
      {
        path: 'equipment/:id',
        element: (
          <ErrorBoundary>
            <LazyPage>
              <EquipmentDetailPage />
            </LazyPage>
          </ErrorBoundary>
        ),
      },
      {
        path: 'vehicles',
        element: (
          <ErrorBoundary>
            <LazyPage>
              <VehiclesPage />
            </LazyPage>
          </ErrorBoundary>
        ),
      },
      {
        path: 'roles',
        element: (
          <ErrorBoundary>
            <LazyPage>
              <RolesPage />
            </LazyPage>
          </ErrorBoundary>
        ),
      },
      {
        path: 'reports',
        element: (
          <ErrorBoundary>
            <LazyPage>
              <ReportsPage />
            </LazyPage>
          </ErrorBoundary>
        ),
      },
      {
        path: 'notifications',
        element: (
          <ErrorBoundary>
            <LazyPage>
              <AdminNotificationsPage />
            </LazyPage>
          </ErrorBoundary>
        ),
      },
      {
        path: 'notification-settings',
        element: (
          <ProtectedRoute requireAdminRole>
            <ErrorBoundary>
              <LazyPage>
                <NotificationSettingsPage />
              </LazyPage>
            </ErrorBoundary>
          </ProtectedRoute>
        ),
      },
      {
        path: 'profile',
        element: (
          <ErrorBoundary>
            <LazyPage>
              <AdminProfilePage />
            </LazyPage>
          </ErrorBoundary>
        ),
      },
    ],
  },

  // Worker routes
  {
    path: '/worker',
    errorElement: <RouteErrorPage />,
    element: (
      <ProtectedRoute>
        <WorkerLayout />
      </ProtectedRoute>
    ),
    children: [
      {
        index: true,
        element: (
          <ErrorBoundary>
            <LazyPage>
              <WorkerHomePage />
            </LazyPage>
          </ErrorBoundary>
        ),
      },
      // Stari email deep linkovi vode na /worker/tasks (bez id) - preusmeri na početnu
      { path: 'tasks', element: <Navigate to="/worker" replace /> },
      {
        path: 'tasks/:id',
        element: (
          <ErrorBoundary>
            <LazyPage>
              <WorkerTaskPage />
            </LazyPage>
          </ErrorBoundary>
        ),
      },
      {
        path: 'notifications',
        element: (
          <ErrorBoundary>
            <LazyPage>
              <WorkerNotificationsPage />
            </LazyPage>
          </ErrorBoundary>
        ),
      },
      {
        path: 'jobs',
        element: (
          <ErrorBoundary>
            <LazyPage>
              <WorkerJobsPage />
            </LazyPage>
          </ErrorBoundary>
        ),
      },
      {
        path: 'schedule',
        element: (
          <ErrorBoundary>
            <LazyPage>
              <WorkerSchedulePage />
            </LazyPage>
          </ErrorBoundary>
        ),
      },
      {
        path: 'profile',
        element: (
          <ErrorBoundary>
            <LazyPage>
              <WorkerProfilePage />
            </LazyPage>
          </ErrorBoundary>
        ),
      },
      {
        path: 'graphics',
        element: (
          <ErrorBoundary>
            <LazyPage>
              <WorkerGraphicsPage />
            </LazyPage>
          </ErrorBoundary>
        ),
      },
      // "Za dodelu" (TASK-48): the head of a sector staffs steps from the phone.
      {
        path: 'assign',
        element: (
          <ErrorBoundary>
            <LazyPage>
              <WorkerAssignPage />
            </LazyPage>
          </ErrorBoundary>
        ),
      },
    ],
  },

  // Root redirect
  { path: '/', element: <RootRedirect />, errorElement: <RouteErrorPage /> },

  // 404
  {
    path: '*',
    element: <NotFoundPage />,
  },
]);
